/**
 * Gobodo Error Handler
 * 
 * Centralized error handling for Gobodo API operations.
 * Provides user-friendly error messages and consistent error handling patterns.
 */

export interface GobodoErrorContext {
  operation: string;
  objectId?: string;
  objectType?: string;
  userId?: string | number;
  roleId?: string | number;
  fieldName?: string;
  [key: string]: any;
}

export class GobodoError extends Error {
  constructor(
    message: string,
    public readonly context?: GobodoErrorContext,
    public readonly originalError?: any
  ) {
    super(message);
    this.name = 'GobodoError';
  }
}

/**
 * Convert Gobodo API errors to user-friendly messages
 * Note: 204 (No Content) is treated as success, not an error
 */
export function handleGobodoError(error: any, context?: GobodoErrorContext): GobodoError | null {
  const operation = context?.operation || 'operation';
  
  // 204 is success, not an error - return null to indicate success
  if (error.response?.status === 204 || error.status === 204) {
    return null;
  }
  
  // Network errors
  if (!error.response && error.message) {
    if (error.message.includes('Network Error') || error.message.includes('Failed to fetch')) {
      return new GobodoError(
        'Unable to connect to the server. Please check your internet connection and try again.',
        context,
        error
      );
    }
  }

  // HTTP status code errors
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data || {};

    switch (status) {
      case 400:
        return new GobodoError(
          data.message || `Invalid request. Please check the data and try again.`,
          context,
          error
        );
      case 401:
        return new GobodoError(
          'Your session has expired. Please log in again.',
          context,
          error
        );
      case 403:
        return new GobodoError(
          'You do not have permission to perform this action.',
          context,
          error
        );
      case 404:
        const resource = context?.objectType || context?.objectId || 'resource';
        return new GobodoError(
          `${resource} not found. It may have been deleted or does not exist.`,
          context,
          error
        );
      case 409:
        return new GobodoError(
          data.message || 'This record already exists or conflicts with existing data.',
          context,
          error
        );
      case 422:
        return new GobodoError(
          data.message || 'The data provided is invalid. Please check all fields and try again.',
          context,
          error
        );
      case 429:
        return new GobodoError(
          'Too many requests. Please wait a moment and try again.',
          context,
          error
        );
      case 500:
      case 502:
      case 503: {
        let msg = typeof data?.message === 'string' ? data.message : data?.error ?? data?.detail;
        if (!msg && typeof data?.stackTrace === 'string') {
          const firstLine = data.stackTrace.split('\n')[0]?.trim() || '';
          const match = firstLine.match(/^[\w.]+:\s*(.+)$/);
          msg = match ? match[1] : firstLine;
        }
        const hint = msg ? ` Server reported: ${String(msg).slice(0, 100)}.` : '';
        const fieldId = context?.fieldId != null ? ` Include field ID ${context.fieldId} when reporting.` : '';
        return new GobodoError(
          `Server error. Please try again later. If the problem persists, contact support.${hint}${fieldId}`,
          context,
          error
        );
      }
      default:
        return new GobodoError(
          data.message || `An error occurred (${status}). Please try again.`,
          context,
          error
        );
    }
  }

  // Generic error
  return new GobodoError(
    error.message || `An unexpected error occurred during ${operation}.`,
    context,
    error
  );
}

/**
 * Pull a Gobodo request/trace ID from an error or a response, looking in the
 * places superagent/axios-style clients surface headers. Returns undefined
 * when nothing is present. Safe on any input.
 */
export function extractRequestId(source: unknown): string | undefined {
  if (!source || typeof source !== 'object') return undefined;
  const s = source as Record<string, any>;

  const headerCandidates: Array<Record<string, any> | undefined> = [
    s.response?.headers,
    s.response?.header,
    s.headers,
  ];
  const keys = ['x-request-id', 'x-trace-id', 'x-correlation-id'];
  for (const h of headerCandidates) {
    if (!h || typeof h !== 'object') continue;
    for (const k of keys) {
      const v = h[k] ?? h[k.toUpperCase()];
      if (typeof v === 'string' && v.length > 0) return v;
    }
  }
  return undefined;
}

/**
 * Log error with context for debugging
 * Handles null errors (204 success responses) gracefully
 */
export function logGobodoError(error: GobodoError | null, logLevel: 'error' | 'warn' = 'error'): void {
  // 204 responses return null - don't log as error
  if (!error) {
    return;
  }

  const requestId = extractRequestId(error.originalError);
  const contextEntries = error.context
    ? Object.entries(error.context).map(([k, v]) => `${k}=${v}`)
    : [];
  if (requestId) contextEntries.push(`requestId=${requestId}`);
  const contextStr = contextEntries.length ? ` [${contextEntries.join(', ')}]` : '';

  const message = `[Gobodo] ${error.message}${contextStr}`;

  if (logLevel === 'error') {
    console.error(message, error.originalError);
  } else {
    console.warn(message, error.originalError);
  }
}

/**
 * Check if error is retryable
 */
export function isRetryableError(error: any): boolean {
  // Parser errors (backend returned HTML) are retryable
  const msg = error?.message ?? '';
  if (msg.includes('Parser') || msg.includes('parse')) return true;

  // Network errors are retryable
  if (msg.includes('Network Error') || msg.includes('Failed to fetch') || msg.includes('ECONNREFUSED')) return true;

  // Check status from multiple possible locations (SDK uses error.status, axios uses error.response.status)
  const status = error?.status ?? error?.response?.status ?? error?.response?.statusCode ?? error?.statusCode;
  if (!status) return false; // unknown shape -- don't retry (likely a client-side bug)

  return status === 429 || status === 500 || status === 502 || status === 503 || status === 504;
}

/**
 * Check if error indicates the resource doesn't exist
 */
export function isNotFoundError(error: any): boolean {
  return error.response?.status === 404 || 
         error.status === 404 ||
         (error instanceof GobodoError && error.message.includes('not found'));
}

/**
 * Check if response is a success status (including 204)
 */
export function isSuccessResponse(status: number): boolean {
  return (status >= 200 && status < 300) || status === 204;
}

/**
 * Check if error indicates authentication failure
 */
export function isAuthError(error: any): boolean {
  return error.response?.status === 401 || error.response?.status === 403;
}

/**
 * Ensure a thrown value is an Error instance. Prevents "[object Object]" in error overlays.
 */
export function ensureError(value: unknown): Error {
  if (value instanceof Error) return value;
  if (value instanceof GobodoError) return value;
  if (typeof value === 'string') return new Error(value);
  const msg = (value as any)?.message ?? (value as any)?.error ?? JSON.stringify(value);
  return new Error(String(msg));
}
